import { createSlice } from "@reduxjs/toolkit";
import { getWebhooksAPI, createWebhookAPI, deleteWebhookAPI } from "./apis/webhooks";
import { normalizeWebhook } from "@utils/webhook-adapters";
import extractApiError from "@utils/extract-api-error";

const initialState = {
    pending: false,
    error: null,
    data: [],
};

const webhooksSlice = createSlice({
    name: "webhooks",
    initialState,
    reducers: {
        resetWebhooks: () => initialState,
        pending(state) {
            state.pending = true;
            state.error = null;
        },
        success(state, action) {
            state.pending = false;
            state.error = null;
            state.data = action.payload;
        },
        failure(state, action) {
            state.pending = false;
            state.error = extractApiError(action.payload);
        },
    },
});

const { pending, success, failure } = webhooksSlice.actions;
export const { resetWebhooks } = webhooksSlice.actions;

/**
 * 웹훅 목록 조회 (페이지 단위).
 * - name / status 는 서버 검색, limit / nextToken 으로 토큰 페이지네이션(한 페이지씩 조회).
 * - 응답(response)을 resolve 하여 호출 측이 response.data.nextToken 으로 페이지별 토큰을 관리한다.
 * - 목록 응답(WebhookSummary)에는 headers/method/description 이 없다 — 상세는 반드시 단건 조회로.
 * @param {object} params - { name, status, limit, nextToken }
 */
export const getWebhooks = (params) => (dispatch, getState) => {
    const { stage, project } = getState();
    dispatch(pending());
    return getWebhooksAPI(stage.id, stage.endpoint, project.id, params)
        .then((response) => {
            dispatch(success((response.data?.results ?? []).map(normalizeWebhook)));
            return response;
        })
        .catch((error) => {
            dispatch(failure(error));
            throw error;
        });
};

/**
 * 웹훅 생성.
 * - 리듀서 상태는 변경하지 않고 API Promise 만 반환 → 호출 측에서 목록 재조회/알림 처리.
 * - 응답은 201 { id } 뿐이라 낙관적 추가가 불가능하다. 반드시 목록을 재조회할 것.
 * @param {object} data - WebhookRequest 전체 { name, description, status, triggers, configuration }
 */
export const createWebhook = (data) => (dispatch, getState) => {
    const { stage, project } = getState();
    return createWebhookAPI(stage.id, stage.endpoint, project.id, data);
};

/**
 * 웹훅 삭제.
 * - 리듀서 상태는 변경하지 않고 API Promise 만 반환 → 호출 측에서 목록 재조회/알림 처리.
 */
export const deleteWebhook = (webhookId) => (dispatch, getState) => {
    const { stage, project } = getState();
    return deleteWebhookAPI(stage.id, stage.endpoint, project.id, webhookId);
};

export default webhooksSlice.reducer;
